import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers } from "@fortawesome/free-solid-svg-icons";
import { Sparkles } from "lucide-react";
import EmployeeFilter from "./EmployeeFilter";
import EmployeeFormModal from "./EmployeeFormModal";
import EmployeeDetailModal from "./EmployeeDetailModal";
import {
    getAllEmployeesNoPageHasFilter,
    getEmployeeById,
    deleteEmployee,
    toggleEmployeeStatus  
} from "../../../api/hrm/employees/employeeInfo";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import { startEmployeeListTour } from "./employeeListTour";

/** API có thể trả `name` hoặc `employee_name` (alias). */
function getEmployeeListName(emp) {
  if (!emp) return "";
  return String(emp.name || emp.employee_name || emp.full_name || "").trim();
}

/**
 * Tên dài / nhiều từ → một dòng dạng "Họ .... Tên" (tránh xuống dòng xấu trên thẻ hẹp).
 */
function formatEmployeeCardName(fullName) {
  const raw = String(fullName ?? "").trim();
  if (!raw) return "—";
  const parts = raw.split(/\s+/).filter(Boolean);
  const longEnough =
    raw.length > 28 || (parts.length >= 5 && raw.length > 20);
  if (parts.length >= 2 && longEnough) {
    return `${parts[0]} .... ${parts[parts.length - 1]}`;
  }
  return raw;
}

function getEmployeeGroupLabel(emp) {
  if (!emp) return "Chưa gán chi nhánh";
  if (emp.branch_name) return emp.branch_name;
  if (emp.teacher_kind === "partner_school" && emp.partner_school_name) {
    return `Đối tác - ${emp.partner_school_name}`;
  }
  return "Chưa gán chi nhánh";
}

const EmployeeCardPage = () => {
  const [employees, setEmployees] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filters, setFilters] = useState({
    keyword: "",
    branch_id: "",
    department_id: "",
    employment_classification: "",
    status: "",
  });
  const [showDetail, setShowDetail] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const fetchEmployees = async () => {
    try {
      const params = {
        keyword: filters.keyword,
        branch_id: filters.branch_id,
        department_id: filters.department_id,
        employment_classification: filters.employment_classification,
        status: filters.status,
      };
  
      const res = await getAllEmployeesNoPageHasFilter(params);
  
      setEmployees(Array.isArray(res) ? res : (res.items || []));
      // nếu có state total:
      // setTotal(res.total || 0);
  
    } catch (err) {
      console.error("Lỗi khi lấy danh sách nhân viên:", err);
      showErrorToast("Không thể tải danh sách nhân viên");
      setEmployees([]); // ⬅️ phòng thủ
    }
  };
  



  useEffect(() => {
    fetchEmployees();
  }, [filters]);

  const handleCreate = () => {
    setEditId(null);
    setShowDrawer(true);
  };

  const handleDrawerClose = (shouldReload = false) => {
    setShowDrawer(false);
    setEditId(null);
    if (shouldReload) fetchEmployees(); // chỉ reload khi cần
  };
  

  const handleView = async (employee) => {
    try {
      const full = await getEmployeeById(employee.id);
      setSelectedEmployee(full);
      setShowDetail(true);
    } catch (err) {
      console.error("Lỗi khi tải chi tiết nhân viên:", err);
      showErrorToast("Không tải được chi tiết nhân viên");
    }
  };


  const handleToggleStatus = async (id, newStatus) => {
    try {
      await toggleEmployeeStatus(id, newStatus);
      showSuccessToast(newStatus ? "Đã mở tài khoản" : "Đã khóa tài khoản");
      setShowDetail(false);
      fetchEmployees();
    } catch {
      showErrorToast("Không thể cập nhật trạng thái tài khoản");
    }
  };

  const visibleEmployees = showInactive
    ? employees
    : employees.filter((emp) => emp.is_active);
  
  return (
    <div className="p-4">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-8" data-tour="hrm-employees-header">
        <div className="flex items-center gap-4">
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon
              icon={faUsers}
              className="text-xl w-6 h-6 flex items-center justify-center"
            />
          </div>

          <div>
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Danh sách <span className="text-red-700">nhân viên</span>
            </h1>

            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{visibleEmployees.length}</span>{" "}
                nhân viên đang hiển thị
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => window.setTimeout(() => startEmployeeListTour(), 80)}
          title="Hướng dẫn nhanh"
          aria-label="Hướng dẫn nhanh"
          className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
        >
          <Sparkles className="h-5 w-5" strokeWidth={2.2} />
          <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
            Hướng dẫn nhanh
          </span>
        </button>
      </div>

      {/* Filter */}
      <div data-tour="hrm-employees-filter">
        <EmployeeFilter
          onNewClick={handleCreate}
          onFilterChange={setFilters}
        />
      </div>
      <div className="mb-5 mt-[-12px] flex items-center justify-end gap-3">
        <p className="text-[11px] text-gray-500">
          Hiển thị nhân sự ngưng sử dụng (mặc định đang ẩn)
        </p>
        <button
          type="button"
          onClick={() => setShowInactive((prev) => !prev)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            showInactive ? "bg-red-600" : "bg-gray-300"
          }`}
          aria-pressed={showInactive}
          title="Hiển thị nhân sự ngưng sử dụng"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
              showInactive ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      {/* ============================
          GROUP LIST BY BRANCH
      =============================== */}
      <div className="mt-4 space-y-10" data-tour="hrm-employees-grid">

      {Object.entries(
        visibleEmployees.reduce((acc, emp) => {
          const groupName = getEmployeeGroupLabel(emp);
          if (!acc[groupName]) acc[groupName] = [];
          acc[groupName].push(emp);
          return acc;
        }, {})
      ).map(([branchName, list]) => {
        const total = list.length;
        const active = list.filter((x) => x.is_active).length;

        return (
          <div key={branchName}>            
            {/* 🔥 GROUP TITLE */}
            <h2 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-3">
              <span className="text-red-600 text-2xl">🏢</span>
              {branchName} 
              <span className="text-sm text-gray-500">
                ({active} / {total})
              </span>
            </h2>

            {/* 🔹GRID CARD LIST */}
            {/* <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 4xl:grid-cols-6 gap-4"> */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
              {list.map((employee) => (
                <div
                  key={employee.id}
                  className="relative bg-white rounded-xl shadow-md p-4 border hover:shadow-lg transition cursor-pointer"
                  onClick={() => handleView(employee)}
                >
                  {/* Badge góc thẻ (absolute), không đè hàng tên/chức vụ → cột chữ full width tới mép phải (chỉ trừ padding thẻ) */}
                  <div
                    className={`
                      absolute top-0 right-0 z-10
                      text-white text-[10px] font-semibold tracking-wide
                      px-3 py-[4px] shadow-sm
                      rounded-bl-lg rounded-tr-xl
                      ${
                        employee.is_active
                          ? "bg-gradient-to-r from-green-400 to-green-500"
                          : "bg-gradient-to-r from-red-400 to-red-500"
                      }
                    `}
                  >
                    {employee.is_active ? "ĐANG LÀM VIỆC" : "NGƯNG SỬ DỤNG"}
                  </div>

                  <div className="mt-4 grid grid-cols-[3.5rem_minmax(0,1fr)] grid-rows-[auto_auto] gap-x-3 gap-y-1 items-start">
                    <img
                      src={
                        employee.avatar_url ||
                        "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                      }
                      className="col-start-1 row-span-2 row-start-1 w-14 h-14 shrink-0 rounded-full border object-cover self-start"
                      alt="avatar"
                    />

                    <div className="col-start-2 row-start-1 min-w-0 overflow-hidden">
                      <div
                        className="font-semibold text-gray-900 text-lg leading-tight truncate"
                        title={getEmployeeListName(employee)}
                      >
                        {formatEmployeeCardName(getEmployeeListName(employee))}
                      </div>
                    </div>

                    <div className="col-start-2 row-start-2 min-w-0 overflow-hidden">
                      <div className="truncate text-gray-500 text-sm">
                        {employee.position_name || "—"}
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="mt-3 text-sm text-gray-700 space-y-1">
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-500 w-20 shrink-0">
                        Chi nhánh:
                      </span>
                      <span className="flex-1 min-w-0 truncate text-gray-700">
                        {employee.branch_name}
                      </span>
                    </div>

                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-500 w-20 shrink-0">
                        Bộ phận:
                      </span>
                      <span className="flex-1 min-w-0 truncate text-gray-700">
                        {employee.department_name || "—"}
                      </span>
                    </div>

                    {/* EMAIL */}
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-500 w-20 shrink-0">Email:</span>
                      <a
                        href={`mailto:${employee.email}`}
                        className="flex-1 truncate text-gray-700 hover:text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {employee.email}
                      </a>
                    </div>

                    {/* PHONE / ZALO: https://zalo.me/phone_number*/}
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-500 w-20 shrink-0">Điện thoại:</span>
                      <a
                        href={`https://zalo.me/${employee.phone}`}
                        className="text-gray-700 hover:text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                        target="_blank"        // mở tab mới (tùy chọn)
                        rel="noopener noreferrer"
                      >
                        {employee.phone}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      </div>


      {/* Modal thêm/sửa */}
      {showDrawer && (
        <EmployeeFormModal
          isOpen={showDrawer}
          onClose={handleDrawerClose}
          employeeId={editId}
        />
      )}

      {/* Modal xem chi tiết */}
      <EmployeeDetailModal
        isOpen={showDetail}
        employee={selectedEmployee}
        onClose={() => setShowDetail(false)}
        onEdit={(id) => {
          // Sửa nhân viên chỉ trên desktop (≥ md)
          if (typeof window !== "undefined" && window.innerWidth < 768) return;
          setEditId(id);
          setShowDetail(false);
          setShowDrawer(true);
        }}
        onDelete={async (id) => {
          try {
            await deleteEmployee(id);
            showSuccessToast("Xóa nhân viên thành công");
            setShowDetail(false);
            fetchEmployees();
          } catch (err) {
            showErrorToast("Không thể xóa nhân viên");
          }
        }}        
        onToggleStatus={handleToggleStatus} // 👈 Thêm dòng này
      />

    </div>
  );
};

export default EmployeeCardPage;